import type { Dispatch, SetStateAction } from "react"
import React, { useState } from "react"

import { useTranslation } from "react-i18next"

import * as Yup from "yup"
import CloseIcon from "@mui/icons-material/Close"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Switch,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { Button, DateRangePicker, InputText, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import { useFormik } from "formik"
import type { Moment } from "moment"
import moment from "moment"

import { getTheme } from "../../../hooks/UseTheme"
import { KonanTimeHelper } from "../../../utils/genericHelpers"

type Props = {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  isLoading?: boolean
  onClose: () => void
  onModelCreation: (
    start_time: string,
    end_time: string,
    append_training_data: boolean,
    resulting_model_name: string,
    resulting_model_state: string,
  ) => void
  retrainingError: string | undefined
}

/**
 * Mini evaluation metrics card
 * @param {boolean} open
 * @param {boolean} isLoading
 * @param {Function} onClose
 * @param {Function} onModelCreation
 * @return {React.ReactElement}
 */
export function RetrainingCreationDialog(props: Readonly<Props>): React.ReactElement {
  const { open, setOpen, onClose, onModelCreation, retrainingError, isLoading } = props
  const { t } = useTranslation("TrainingJob")

  const muiTheme = useTheme()
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down("md"))

  const theme = getTheme()

  const konanTime = new KonanTimeHelper()

  const [startDate, setStartDate] = useState(konanTime.adjustDate(moment().subtract(30, "days"), "start"))
  const [endDate, setEndDate] = useState(konanTime.adjustDate(moment(), "end"))

  // Yup validation object
  const validationSchema = Yup.object({
    name: Yup.string()
      .max(30, t("validation.maxLength", { max: 30 }))
      .required(t("validation.required")),
    appendTrainingData: Yup.boolean(),
  })

  // Form control
  const formik = useFormik({
    initialValues: {
      name: "",
      appendTrainingData: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Add Retraining Data
      if (startDate && endDate) {
        const Model: [string, string, boolean, string, string] = [
          startDate.toISOString(),
          endDate.toISOString(),
          values.appendTrainingData,
          values.name,
          "disabled",
        ]
        onModelCreation(...Model)
      }
    },
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          // To enable the date-picker to overflow
          overflow: "unset",
        },
      }}
    >
      <DialogTitle className="alt-dialog-header-base">
        <Grid item container justifyContent="space-between" alignItems={"center"} xs={12}>
          <Grid item>
            <Typography variant="h2-bold">{t("dialogs.retraining.title")}</Typography>
          </Grid>

          <Grid item>
            <IconButton id="close" onClick={() => setOpen(false)} size="small" className={"close-icon-button"}>
              <CloseIcon style={{ color: theme.palette.gray.background[1] }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent className="dialog-content-base">
        {retrainingError && <Snackbar severity="error" fullWidth description={retrainingError} />}
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="label-bold">{t("dialogs.retraining.servingData")}</Typography>
          </Grid>

          <Grid item xs={12} className="data-popper-modifier">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={(startDate) => setStartDate(konanTime.adjustDate(startDate, "start") as Moment)}
              onEndDateChange={(endDate) => setEndDate(konanTime.adjustDate(endDate, "end") as Moment)}
              disableFuture
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="label-bold">{t("dialogs.retraining.appendTrainingData")}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Switch
              checked={formik.values.appendTrainingData}
              onChange={formik.handleChange}
              name="appendTrainingData"
              color="primary"
            />
          </Grid>

          <Grid item xs={12}>
            <InputText
              id="name"
              label={t("dialogs.retraining.name")}
              placeholder={t("dialogs.retraining.namePlaceholder")}
              value={formik.values.name}
              handleChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name) && formik.errors.name}
              fullWidth
              required
              description={t("dialogs.retraining.description")}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className="alt-dialog-actions-base">
        <Button onClick={onClose} variant={"secondary"}>
          {t("dialogs.retraining.cancel")}
        </Button>
        <Button
          onClick={formik.submitForm}
          variant="primary"
          disabled={!formik.isValid || !startDate || !endDate || isLoading || !formik.values.name}
          isLoading={isLoading}
        >
          {t("dialogs.retraining.create")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
